/**
 * Single source of truth for spreadsheet/CSV column recognition.
 *
 * Pure TypeScript with no runtime dependencies, so every package can import it:
 * `@tn/core` (the ExcelJS workbook walker), `@tn/desktop` + `@tn/web` (transport),
 * and `@tn/ui` (CSV upload). The dictionary lives here rather than in `@tn/core`
 * because `@tn/ui` depends only on `@tn/shared` — putting it in core would leave
 * the UI's CSV parser holding its own duplicate copy.
 */

import type { DashboardData } from './types'
import type { DuplicateRoleGroup, SimilarRolePair } from './roster-validation'

// ── Fields ───────────────────────────────────────────────────────────────────

export type ColumnField =
  | 'subfunction'
  | 'role'
  | 'task'
  | 'description'
  | 'fte'
  | 'mode'
  | 'skills'
  | 'time_pct'
  | 'hours'
  | 'cost'
  | 'value'

/**
 * Accepted header spellings per field, in priority order.
 *
 * Entries are stored already normalized (see `normalizeHeader`) — lowercase with
 * `-`, `_` and `/` collapsed to single spaces. That is why `job_title` and
 * `Sub-Function` need no entries of their own: they normalize onto `job title`
 * and `sub function`.
 */
export const COLUMN_SYNONYMS: Record<ColumnField, readonly string[]> = {
  subfunction: [
    'subfunction', 'subfunctions',
    'sub function', 'sub functions',
    'sub function group', 'sub function groups',
    'subfunction group', 'subfunction groups',
    'function', 'functions',
    'function group', 'function groups',
    'department', 'departments',
    'job family', 'job families',
    'job family group', 'job family groups',
  ],
  role: [
    'job profile name', 'job profile names',
    'job profile', 'job profiles',
    'role',
    'roles',
    'job role',
    'job roles',
    'job title', 'job titles',
    'title', 'titles',
    'position', 'positions',
  ],
  task: ['task name', 'task', 'tasks', 'activity', 'activities', 'responsibility', 'responsibilities'],
  description: ['job description', 'job descriptions', 'description', 'descriptions', 'summary', 'summaries', 'role description', 'role descriptions'],
  fte: [
    'fte', 'ftes', 'fte count',
    'headcount', 'head count', 'headcounts',
    'est headcount', 'estimated headcount',
    'est hc', 'estimated hc', 'hc',
    'employee count', 'employees',
  ],
  mode: ['mode', 'automation mode', 'automation type', 'work type', 'category'],
  skills: ['skills', 'skill'],
  time_pct: ['time pct', 'time %', 'time'],
  hours: ['hours', 'annual hours'],
  cost: ['cost'],
  value: ['value', 'priority'],
}

/**
 * Grand-total and subtotal rows carry the whole workforce in their headcount
 * cell, so ingesting one as data doubles the total. Matched against the
 * *entire* normalized cell rather than as a substring — a real subfunction
 * named "Total Rewards & People Operations" must not be mistaken for one.
 *
 * Shared so the spreadsheet and CSV importers cannot drift apart on it.
 */
const TOTAL_ROW_LABEL = /^(grand\s+|overall\s+|sub[\s-]?)?totals?(\s+(fte|ftes|headcount|head\s+count|hc))?$/i

export function isTotalRowLabel(value: string): boolean {
  const normalized = String(value ?? '').replace(/[:.]+$/, '').replace(/\s+/g, ' ').trim()
  return normalized.length > 0 && TOTAL_ROW_LABEL.test(normalized)
}

/**
 * Whether a repeat row for an already-seen (subfunction, role) pair represents
 * *more people* rather than more detail about the same people.
 *
 * A role legitimately reappears for two different reasons and only one of them
 * adds headcount:
 *   - a task row (role repeated to hang another task off it), or a continuation
 *     row where the role cell was left blank — headcount already counted
 *   - a second headcount line naming the role again with no task — a genuinely
 *     separate group of people
 *
 * Shared so every importer applies the same rule. Summing unconditionally
 * inflates any roster that lists one role across several task rows.
 */
export function repeatRowAddsHeadcount(opts: { roleNamedInRow: boolean; hasTask: boolean }): boolean {
  return opts.roleNamedInRow && !opts.hasTask
}

/** Fields a row must supply for the dashboard schema to be buildable. */
export const REQUIRED_FIELDS: readonly ColumnField[] = ['role']

/**
 * A role column alone is ambiguous — a header row also needs either a grouping
 * column or some task/description context before we treat it as the data table.
 */
export const CONTEXT_FIELDS: readonly ColumnField[] = ['subfunction', 'task', 'description']

/** Human-facing label per field, used when building error messages. */
export const FIELD_LABELS: Record<ColumnField, string> = {
  subfunction: 'Subfunction',
  role: 'Role',
  task: 'Task',
  description: 'Description',
  fte: 'FTE',
  mode: 'Mode',
  skills: 'Skills',
  time_pct: 'Time %',
  hours: 'Hours',
  cost: 'Cost',
  value: 'Value',
}

// ── Structured notices ───────────────────────────────────────────────────────

export type ParseNoticeCode =
  | 'fuzzy-column-match'
  | 'ambiguous-column'
  | 'sheet-skipped'
  | 'unrecognised-columns'
  | 'missing-required-column'
  | 'no-data-rows'
  | 'total-row-excluded'
  | 'duplicate-roles-merged'
  | 'headcount-column-missing'
  | 'invalid-cell-value'
  | 'excel-incompatible-format'

/**
 * One structured piece of parser feedback. Typed rather than free-form so the UI
 * can group, filter and style notices instead of printing a blob of text.
 */
export interface ParseNotice {
  code: ParseNoticeCode
  /** Human-readable sentence, safe to render directly. */
  message: string
  /** Worksheet the notice came from, when applicable. */
  sheet?: string
  /** Field the notice concerns, when applicable. */
  field?: ColumnField
  /** The header exactly as it appeared in the file. */
  detectedHeader?: string
  /** The canonical spelling we think was meant (did-you-mean). */
  suggestion?: string
  /** Every header found on the sheet — used by error notices for context. */
  detectedHeaders?: string[]
  /** Columns with a blank header on a skipped sheet; above 0 means the sheet had data but no usable headers. */
  blankHeaderCount?: number
  /** 1-based spreadsheet row the notice concerns, when applicable. */
  row?: number
}

export interface ExcelParseResult {
  data: DashboardData
  /** Non-blocking: parsing succeeded, but the user should know something. */
  warnings: ParseNotice[]
  /** Blocking: nothing usable was produced. */
  errors: ParseNotice[]
  /** All non-empty column headers detected in the file, in original order. */
  rawHeaders?: string[]
  /** Up to 3 sample cell values per column header, for mapping confirmation UI. */
  columnSamples?: Record<string, string[]>
  /** Raw row count summed across all sheets (both parsed and skipped). Used for headcount reconciliation. */
  sourceRowCount?: number
  /** Report-only: (subfunction, role) pairs listed more than once in this roster. */
  duplicateRoles?: DuplicateRoleGroup[]
  /** Report-only: non-identical role names in one subfunction that may be the same role. */
  similarRoles?: SimilarRolePair[]
}

// ── Normalization ────────────────────────────────────────────────────────────

/**
 * Fold a raw header into its comparison form: lowercase, separators collapsed to
 * single spaces, surrounding whitespace removed. This alone fixes a large share
 * of real-world near-misses (`Sub-Function`, `job_title`, `Task  Name`).
 */
export function normalizeHeader(raw: string): string {
  return String(raw ?? '')
    .toLowerCase()
    .replace(/[-_/\\]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

// ── Similarity (near-miss matching) ──────────────────────────────────────────

/**
 * Damerau-Levenshtein distance — Levenshtein plus adjacent transpositions.
 * Transpositions matter here because the common spreadsheet typo is a swap
 * (`Roel` for `Role`), which plain Levenshtein charges two edits for.
 */
export function damerauLevenshtein(a: string, b: string): number {
  const rows = a.length + 1
  const cols = b.length + 1
  const d: number[][] = Array.from({ length: rows }, () => new Array<number>(cols).fill(0))

  for (let i = 0; i < rows; i++) d[i][0] = i
  for (let j = 0; j < cols; j++) d[0][j] = j

  for (let i = 1; i < rows; i++) {
    for (let j = 1; j < cols; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost)
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + cost)
      }
    }
  }
  return d[a.length][b.length]
}

/**
 * Similarity in [0, 1] between two already-normalized headers.
 *
 * Token containment is checked first and deliberately outranks edit distance:
 * `role name` vs `role` scores only 0.44 on edit distance because the lengths
 * differ, yet it is an obvious match to a human. Edit distance is the fallback,
 * and is what catches single-character typos and plurals.
 */
export function headerSimilarity(a: string, b: string): number {
  if (a === b) return 1

  const aTokens = a.split(' ').filter(Boolean)
  const bTokens = b.split(' ').filter(Boolean)
  if (aTokens.length && bTokens.length) {
    const aSet = new Set(aTokens)
    const bSet = new Set(bTokens)
    const shorter = aTokens.length <= bTokens.length ? aSet : bSet
    const longer = aTokens.length <= bTokens.length ? bSet : aSet
    let contained = true
    for (const token of shorter) {
      if (!longer.has(token)) {
        contained = false
        break
      }
    }
    // Every token of the shorter header appears in the longer one, e.g.
    // "role name" ⊇ "role". Scale by how much extra noise the longer one carries
    // so an exact token match still beats a padded one.
    //
    // The 0.6 floor and 0.4 span are chosen against FUZZY_THRESHOLD so that
    // matching half the tokens clears the bar ("role name" → role = 0.80) while
    // matching a third does not ("cost centre code" → cost = 0.73). Capped below
    // 1 so a token-order variant is still reported as a did-you-mean rather than
    // passing silently as an exact hit.
    if (contained) return Math.min(0.99, 0.6 + 0.4 * (shorter.size / longer.size))
  }

  const distance = damerauLevenshtein(a, b)
  return 1 - distance / Math.max(a.length, b.length, 1)
}

/** Minimum similarity before a near-miss is accepted as a match. */
export const FUZZY_THRESHOLD = 0.75

/**
 * A candidate must beat the runner-up from a *different* field by this margin.
 * Without it, `job family` could silently resolve as `job family group`.
 */
export const FUZZY_MARGIN = 0.08

// ── Resolution ───────────────────────────────────────────────────────────────

export interface ColumnMatch {
  field: ColumnField
  /** Header exactly as it appeared in the sheet. */
  header: string
  /** 0-based column index within the header row. */
  index: number
  confidence: 'exact' | 'fuzzy'
  /** Dictionary entry that matched. */
  matchedSynonym: string
  score: number
}

export interface ColumnResolution {
  /**
   * Every column that resolved to a field, keyed by field. Ordered best-first:
   * exact before fuzzy, then by dictionary priority, then by column position.
   * A list rather than a single match because sheets legitimately carry more
   * than one column per field (e.g. both `Task` and `Activity`), and the row
   * reader falls through to the next one when a cell is blank.
   */
  matches: Map<ColumnField, ColumnMatch[]>
  /** Non-empty headers that matched nothing. */
  unrecognised: Array<{ header: string; index: number }>
  /** Near-misses that were too close to call, so deliberately left unmatched. */
  ambiguous: Array<{ header: string; index: number; candidates: ColumnField[] }>
  /** Every non-empty header on the row, in order. */
  detectedHeaders: string[]
  /** Columns whose header cell was empty; tracked so messages can mention them. */
  blankHeaders: Array<{ label: string; index: number }>
}

interface ScoredCandidate {
  field: ColumnField
  synonym: string
  score: number
  priority: number
}

function scoreHeader(normalized: string, fuzzy: boolean): ScoredCandidate[] {
  const candidates: ScoredCandidate[] = []
  for (const field of Object.keys(COLUMN_SYNONYMS) as ColumnField[]) {
    const synonyms = COLUMN_SYNONYMS[field]
    for (let i = 0; i < synonyms.length; i++) {
      const synonym = synonyms[i]
      if (synonym === normalized) {
        candidates.push({ field, synonym, score: 1, priority: i })
        break
      }
      if (!fuzzy) continue
      const score = headerSimilarity(normalized, synonym)
      if (score >= FUZZY_THRESHOLD) {
        candidates.push({ field, synonym, score, priority: i })
      }
    }
  }
  return candidates.sort((l, r) => r.score - l.score || l.priority - r.priority)
}

/**
 * Map a header row onto dashboard fields.
 *
 * Runs once per sheet at header-detection time — never per data row — which is
 * what keeps near-miss matching off the hot path.
 */
export function resolveColumns(
  rawHeaders: string[],
  opts: { fuzzy?: boolean } = {}
): ColumnResolution {
  const fuzzy = opts.fuzzy ?? true
  const matches = new Map<ColumnField, ColumnMatch[]>()
  const unrecognised: ColumnResolution['unrecognised'] = []
  const ambiguous: ColumnResolution['ambiguous'] = []
  const detectedHeaders: string[] = []
  const blankHeaders: ColumnResolution['blankHeaders'] = []

  // Only track blank columns that sit *between* non-blank headers, not trailing
  // empty cells that ExcelJS includes up to the sheet's declared width.
  const lastNonBlankIndex = rawHeaders.reduce(
    (max, h, i) => (String(h ?? '').trim() ? i : max), -1
  )

  rawHeaders.forEach((header, index) => {
    const trimmed = String(header ?? '').trim()
    if (!trimmed) {
      if (index < lastNonBlankIndex) {
        blankHeaders.push({ label: `(blank — column ${index + 1})`, index })
      }
      return
    }
    detectedHeaders.push(trimmed)

    const normalized = normalizeHeader(trimmed)
    if (!normalized) return

    const candidates = scoreHeader(normalized, fuzzy)
    if (candidates.length === 0) {
      unrecognised.push({ header: trimmed, index })
      return
    }

    const best = candidates[0]
    // Only an inexact winner can be ambiguous — an exact hit is definitive.
    if (best.score < 1) {
      const rival = candidates.find((c) => c.field !== best.field)
      if (rival && best.score - rival.score < FUZZY_MARGIN) {
        ambiguous.push({ header: trimmed, index, candidates: [best.field, rival.field] })
        return
      }
    }

    const entry: ColumnMatch = {
      field: best.field,
      header: trimmed,
      index,
      confidence: best.score === 1 ? 'exact' : 'fuzzy',
      matchedSynonym: best.synonym,
      score: best.score,
    }
    const list = matches.get(best.field)
    if (list) list.push(entry)
    else matches.set(best.field, [entry])
  })

  // Best-first within each field so the row reader falls through blanks sensibly.
  for (const list of matches.values()) {
    list.sort(
      (l, r) =>
        r.score - l.score ||
        COLUMN_SYNONYMS[l.field].indexOf(l.matchedSynonym) -
          COLUMN_SYNONYMS[r.field].indexOf(r.matchedSynonym) ||
        l.index - r.index
    )
  }

  return { matches, unrecognised, ambiguous, detectedHeaders, blankHeaders }
}

/**
 * Whether a resolved row looks like the real table header: a role column plus at
 * least one grouping or task/description column. Mirrors the rule the original
 * desktop and web parsers used, so well-formed workbooks behave identically.
 */
export function isUsableHeaderRow(resolution: ColumnResolution): boolean {
  const hasRequired = REQUIRED_FIELDS.every((field) => (resolution.matches.get(field)?.length ?? 0) > 0)
  const hasContext = CONTEXT_FIELDS.some((field) => (resolution.matches.get(field)?.length ?? 0) > 0)
  return hasRequired && hasContext
}

/**
 * Turn a failed resolution into notices that name the problem, replacing the
 * column-blind "does not match the expected format" message.
 */
export function describeMissingColumns(
  resolution: ColumnResolution | null,
  context: { sheet?: string } = {}
): ParseNotice[] {
  const notices: ParseNotice[] = []
  const detectedHeaders = resolution?.detectedHeaders ?? []
  const blankHeaders = resolution?.blankHeaders ?? []

  const missingRequired = REQUIRED_FIELDS.filter(
    (field) => (resolution?.matches.get(field)?.length ?? 0) === 0
  )
  const hasContext = CONTEXT_FIELDS.some((field) => (resolution?.matches.get(field)?.length ?? 0) > 0)

  for (const field of missingRequired) {
    const blankNote = blankHeaders.length > 0
      ? ` Your file also has ${blankHeaders.length} column${blankHeaders.length > 1 ? 's' : ''} with a blank header — if your ${FIELD_LABELS[field].toLowerCase()} data is in one of them, add a column name (e.g. "${COLUMN_SYNONYMS[field][0]}") and re-upload.`
      : ''
    notices.push({
      code: 'missing-required-column',
      field,
      sheet: context.sheet,
      detectedHeaders,
      message:
        `No ${FIELD_LABELS[field]} column was found. ` +
        `Accepted names: ${COLUMN_SYNONYMS[field].slice(0, 6).join(', ')}. ` +
        (detectedHeaders.length
          ? `Headers found in your file: ${detectedHeaders.join(', ')}.`
          : 'No column headers were found in the scanned rows.') +
        blankNote,
    })
  }

  if (missingRequired.length === 0 && !hasContext) {
    notices.push({
      code: 'missing-required-column',
      field: 'subfunction',
      sheet: context.sheet,
      detectedHeaders,
      message:
        'A Role column was found, but no Subfunction, Task or Description column to go with it. ' +
        `Add one of: ${[...COLUMN_SYNONYMS.subfunction.slice(0, 4), ...COLUMN_SYNONYMS.task.slice(0, 3)].join(', ')}. ` +
        (detectedHeaders.length ? `Headers found in your file: ${detectedHeaders.join(', ')}.` : ''),
    })
  }

  return notices
}

/**
 * Score how likely a set of raw column headers belong to a talent roster.
 *
 * A file is considered relevant if it contains a `role` column or at least two
 * other recognised talent fields. Files that score below this threshold
 * (e.g. a revenue report, an invoice) get a warning before import proceeds.
 */
export function scoreFileRelevance(rawHeaders: string[]): {
  isRelevant: boolean
  matchedFieldCount: number
  totalHeaders: number
} {
  const totalHeaders = rawHeaders.filter((h) => String(h ?? '').trim()).length
  if (totalHeaders === 0) return { isRelevant: false, matchedFieldCount: 0, totalHeaders: 0 }

  const matchedFields = new Set<ColumnField>()
  for (const header of rawHeaders) {
    const normalized = normalizeHeader(header)
    if (!normalized) continue
    for (const field of Object.keys(COLUMN_SYNONYMS) as ColumnField[]) {
      if (matchedFields.has(field)) continue
      for (const synonym of COLUMN_SYNONYMS[field]) {
        if (headerSimilarity(normalized, synonym) >= FUZZY_THRESHOLD) {
          matchedFields.add(field)
          break
        }
      }
    }
  }

  const matchedFieldCount = matchedFields.size
  const isRelevant = matchedFields.has('role') || matchedFieldCount >= 2

  return { isRelevant, matchedFieldCount, totalHeaders }
}

/** Fields that say who, where and how many — the columns a roster sheet is built from. */
const ROSTER_FIELDS: readonly ColumnField[] = ['role', 'subfunction', 'fte']

/**
 * Whether a sheet's headers look like roster data: at least one header matches a
 * role, subfunction or headcount column, exactly or within FUZZY_THRESHOLD.
 *
 * Used on sheets the parser skipped. A roster-like sheet that could not be read
 * would silently drop people from the headcount, so it must block; a Notes,
 * Legend or Lookup sheet (e.g. "Field, Value") can safely be left out.
 */
export function isRosterLikeSheet(headers: readonly string[]): boolean {
  return headers.some((header) => {
    const normalized = normalizeHeader(header)
    if (!normalized) return false
    return ROSTER_FIELDS.some((field) =>
      COLUMN_SYNONYMS[field].some((synonym) => headerSimilarity(normalized, synonym) >= FUZZY_THRESHOLD)
    )
  })
}

/**
 * Did-you-mean warnings for every column matched by similarity rather than
 * exactly, plus the near-misses that were too close to call.
 */
export function describeFuzzyMatches(
  resolution: ColumnResolution,
  context: { sheet?: string } = {}
): ParseNotice[] {
  const notices: ParseNotice[] = []

  for (const list of resolution.matches.values()) {
    for (const match of list) {
      if (match.confidence !== 'fuzzy') continue
      notices.push({
        code: 'fuzzy-column-match',
        field: match.field,
        sheet: context.sheet,
        detectedHeader: match.header,
        suggestion: match.matchedSynonym,
        message:
          `We didn't find ${FIELD_LABELS[match.field]} or any of its accepted synonyms ` +
          `in your Excel sheet, but we found "${match.header}" with a confidence level of ` +
          `${Math.round(match.score * 100)}%. Use the dropdown to confirm the mapping.`,
      })
    }
  }

  for (const entry of resolution.ambiguous) {
    notices.push({
      code: 'ambiguous-column',
      sheet: context.sheet,
      detectedHeader: entry.header,
      message:
        `Column "${entry.header}" could be ${entry.candidates
          .map((f) => FIELD_LABELS[f])
          .join(' or ')} — it was too close to call, so it was ignored. ` +
        'Rename it to remove the ambiguity.',
    })
  }

  return notices
}
